// Utility functions for workflow components

// Security utilities
export const sanitizeSVGPath = (path) => {
  if (typeof path !== 'string') return '';
  // Remove potentially dangerous characters and validate SVG path format
  return path.replace(/[<>'"&]/g, '').replace(/[^MmLlHhVvCcSsQqTtAaZz0-9\s.,-]/g, '');
};

export const validateNodeData = (data) => {
  if (!data || typeof data !== 'object') return false;
  // Basic validation - check for common node data properties
  // Agent nodes have 'label' or 'agentType', config nodes have 'configType', input/output have 'title'
  return data.hasOwnProperty('label') || 
         data.hasOwnProperty('agentType') || 
         data.hasOwnProperty('configType') || 
         data.hasOwnProperty('title') ||
         data.hasOwnProperty('handlerName') || // Tools have handlerName
         data.hasOwnProperty('llmType'); // LLMs have llmType
};

// Helper function to normalize projectDTO/appInfo to consistent structure
export const showWorkflowData = (data) => {
  if (!data) {
    return null;
  }
  
  // If it already has appConfigDTOList structure, return as is
  if (data.appConfigDTOList && Array.isArray(data.appConfigDTOList) && data.appConfigDTOList.length > 0) {
    return data;
  }
  
  // If response has workFlowTemplateDTO (API response structure)
  if (data.workFlowTemplateDTO) {
    const workflowData = data.workFlowTemplateDTO;
    const normalized = {
      ...data,
      ...(data.projectDTO || {}),
      appConfigDTOList: [{
        nodes: workflowData.nodes || [],
        edges: workflowData.edges || []
      }]
    };
    return normalized;
  }
  
  // If projectDTO has workflow data directly, wrap it in appConfigDTOList format
  if (data.nodes || data.edges) {
    return {
      ...data,
      appConfigDTOList: [{
        nodes: data.nodes || [],
        edges: data.edges || []
      }]
    };
  }
  
  // If projectDTO has workflowConfig or similar nested structure
  if (data.workflowConfig) {
    return {
      ...data,
      appConfigDTOList: [{
        nodes: data.workflowConfig.nodes || [],
        edges: data.workflowConfig.edges || []
      }]
    };
  }
  
  // If projectDTO has projectInfo.workflow or similar
  if (data.projectInfo?.workflow) {
    return {
      ...data,
      appConfigDTOList: [{
        nodes: data.projectInfo.workflow.nodes || [],
        edges: data.projectInfo.workflow.edges || []
      }]
    };
  }
  
  return data;
};

export const normalizeAgentTitle = (title) => (title || '').trim().toLowerCase();

export const isDuplicateAgentTitle = (title, nodes, excludeNodeId) => {
  const normalized = normalizeAgentTitle(title);
  if (!normalized) return false;

  return (nodes || []).some(
    (node) =>
      (node.type === 'agent' || node.type === 'task') &&
      node.id !== excludeNodeId &&
      normalizeAgentTitle(node.data?.title) === normalized
  );
};

// Event system to replace global window pollution
export const createEventSystem = () => {
  const listeners = new Map();
  
  return {
    addEventListener: (event, handler) => {
      if (!listeners.has(event)) {
        listeners.set(event, new Set());
      }
      listeners.get(event).add(handler);
    },
    removeEventListener: (event, handler) => {
      if (listeners.has(event)) {
        listeners.get(event).delete(handler);
      }
    },
    dispatchEvent: (event, detail) => {
      if (listeners.has(event)) {
        listeners.get(event).forEach(handler => {
          try {
            handler({ detail });
          } catch (error) {
            console.error(`Error in event handler for ${event}:`, error);
          }
        });
      }
    },
    cleanup: () => {
      listeners.clear();
    }
  };
};

export const isCustomLlmType = (llmType) => String(llmType || '').toLowerCase() === 'custom';

export const normalizeLlmType = (llmType) =>
  isCustomLlmType(llmType) ? 'custom' : llmType;
